import { ModalGenerarOC } from "./ComprasUtils";
import CampanaNotificaciones from "./CampanaNotificaciones";
import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { useState, useEffect, useMemo } from "react";
import { logAction } from "./ALQOSLogger";
import { subirDatos, eliminarDato } from "./syncSupabase";

// ─── CONSTANTES ───────────────────────────────────────────────
const STORAGE_TPM = 'alqos_tpm';
const STORAGE_NC = 'alqos_ncs';
const STORAGE_MAQUINAS = 'alqos_maquinas';

const LOG = {
  info: (msg, data) => console.log(`[TPM][INFO] ${msg}`, data || ''),
  error: (msg, err) => console.error(`[TPM][ERROR] ${msg}`, err || ''),
};

const storage = {
  get: (key) => { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; } },
  set: (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); return true; } catch { return false; } },
};

// ─── PILARES TPM ──────────────────────────────────────────────
const PILARES_TPM = {
  autonomo: { label: "🔧 Mantenimiento Autónomo", color: "#22c55e", desc: "Operadores realizan limpieza, inspección y ajustes básicos" },
  planificado: { label: "📅 Mantenimiento Planificado", color: "#3b82f6", desc: "Mantenimiento preventivo programado por técnicos" },
  mejora: { label: "📈 Mejoras Enfocadas", color: "#7B5BF2", desc: "Eliminación de pérdidas crónicas" },
  calidad: { label: "✅ Mantenimiento de Calidad", color: "#f59e0b", desc: "Cero defectos desde el mantenimiento" },
  educacion: { label: "🎓 Educación y Formación", color: "#06b6d4", desc: "Capacitación de operadores y técnicos" },
  seguridad: { label: "🦺 Seguridad y Medio Ambiente", color: "#e63946", desc: "Cero accidentes, cero contaminación" },
};

const TIPOS_MANT = {
  preventivo: { label: "🛡️ Preventivo", color: "#22c55e" },
  correctivo: { label: "🔧 Correctivo", color: "#e63946" },
  predictivo: { label: "📡 Predictivo", color: "#3b82f6" },
  autonomo: { label: "👷 Autónomo", color: "#f59e0b" },
};

const ESTADOS_OT = {
  pendiente: { label: "⏳ Pendiente", color: "#f59e0b" },
  en_curso: { label: "🔵 En curso", color: "#3b82f6" },
  completado: { label: "✅ Completado", color: "#22c55e" },
  cancelado: { label: "⛔ Cancelado", color: "#e63946" },
};

function fechaHoy() { return new Date().toISOString().slice(0, 10); }

function calcularOEE(disponibilidad, rendimiento, calidad) {
  const d = Number(disponibilidad) || 0;
  const r = Number(rendimiento) || 0;
  const c = Number(calidad) || 0;
  return Math.round(d * r * c / 10000);
}

function nivelOEE(oee) {
  if (oee >= 85) return { label: "🟢 Clase Mundial", color: "#22c55e" };
  if (oee >= 65) return { label: "🟡 Aceptable", color: "#f59e0b" };
  return { label: "🔴 Mejorable", color: "#e63946" };
}

function calcularMTTR(registros) {
  const completados = registros.filter(r => r.tipo === "correctivo" && r.tiempoReparacion);
  if (!completados.length) return 0;
  const total = completados.reduce((a, r) => a + (Number(r.tiempoReparacion) || 0), 0);
  return Math.round(total / completados.length);
}

function calcularMTBF(registros) {
  const completados = registros.filter(r => r.tipo === "correctivo" && r.tiempoEntreFallas);
  if (!completados.length) return 0;
  const total = completados.reduce((a, r) => a + (Number(r.tiempoEntreFallas) || 0), 0);
  return Math.round(total / completados.length);
}

function generarIdTPM(lista) {
  const anio = new Date().getFullYear();
  return `TPM-${anio}-${String(lista.length + 1).padStart(4, "0")}`;
}

// Bucket donde vive la foto de un correctivo generado desde un reporte de
// planta (Maquinas.js -- convertirACorrectivoTPM()). No se re-sube a otro
// lado al convertir: el correctivo solo guarda el mismo {path,nombre,tipo}
// que ya apunta a este bucket, mismo patrón que verFotoReporte() en Maquinas.js.
const BUCKET_REPORTES_MAQUINA = "reportes-maquina";

async function verFotoTPM(foto) {
  if (!foto?.path) return;
  try {
    const { supabase } = await import("./supabase");
    const { data, error } = await supabase.storage.from(BUCKET_REPORTES_MAQUINA).createSignedUrl(foto.path, 300);
    if (error || !data?.signedUrl) { alert("No se pudo abrir la foto."); return; }
    window.open(data.signedUrl, "_blank");
  } catch (e) { alert("No se pudo abrir la foto."); }
}

// Un reporte de planta puede traer más de un adjunto (foto + documento) --
// mismo helper que adjuntosDe() en Maquinas.js, duplicado acá porque no es
// exportado (closure privado del módulo, mismo criterio ya usado para
// generarIdFMEAMaquina/generarIdTPMDesdeReporte).
function adjuntosDe(obj) {
  if (obj?.adjuntos?.length) return obj.adjuntos;
  if (obj?.foto) return [obj.foto];
  return [];
}

export default function TPMMantenimiento({ onVolver, oscuro, usuario }) {
  const [modalOCTPM, setModalOCTPM] = useState(null);
 const [registros, setRegistros] = useState([]);
const [cargandoTPM, setCargandoTPM] = useState(true);
useEffect(() => {
  async function cargar() {
    try {
      const { supabase } = await import('./supabase');
      const { data } = await supabase.from('tpm').select('data');
      if (data && data.length > 0) setRegistros(data.map(r => r.data));
      else setRegistros(storage.get(STORAGE_TPM));
    } catch(e) { setRegistros(storage.get(STORAGE_TPM)); }
    setCargandoTPM(false);
  }
  cargar();
}, []);
  // Catálogo de máquinas para vincular un correctivo a maquinaId (indicador
  // de confiabilidad en Maquinas.js) -- solo lectura acá, no se crea ni edita.
  const [maquinas, setMaquinas] = useState([]);
  useEffect(() => {
    async function cargarMaquinas() {
      try {
        const { supabase } = await import('./supabase');
        const { data } = await supabase.from('maquinas').select('data');
        if (data?.length) setMaquinas(data.map(r => r.data));
        else setMaquinas(storage.get(STORAGE_MAQUINAS));
      } catch (e) { setMaquinas(storage.get(STORAGE_MAQUINAS)); }
    }
    cargarMaquinas();
  }, []);
  const [vista, setVista] = useState("dashboard");
  const [form, setForm] = useState({ tipo: "preventivo", pilar: "planificado", estado: "pendiente", fecha: fechaHoy() });
  const [detalle, setDetalle] = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [errores, setErrores] = useState({});
  const [oeeForm, setOeeForm] = useState({ disponibilidad: "", rendimiento: "", calidad: "" });

  const bg = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg = oscuro ? "#0d2160" : "white";
  const texto = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde = oscuro ? "#1e3a8a" : "#eee";
  const navBg = oscuro ? "#060f30" : "#09184D";
  const subtexto = oscuro ? "#9aabd4" : "#808085";
  const esAdmin = usuario?.rol === "Administrador";
  const inp = { width: "100%", padding: "8px 12px", borderRadius: "8px", border: `1px solid ${borde}`, fontSize: "13px", boxSizing: "border-box", marginTop: "4px", background: cardBg, color: texto, fontFamily: "sans-serif" };
  const lbl = { fontSize: "12px", color: oscuro ? "#9aabd4" : "#555", fontWeight: "600", display: "block", marginTop: "12px" };
  const card = { background: cardBg, borderRadius: "12px", padding: "1.5rem", border: `1px solid ${borde}`, marginBottom: "12px" };

  const kpis = useMemo(() => {
    const hoy = new Date();
    const mesActual = hoy.getMonth();
    const anioActual = hoy.getFullYear();
    return {
      total: registros.length,
      pendientes: registros.filter(r => r.estado === "pendiente").length,
      completados: registros.filter(r => r.estado === "completado").length,
      vencidos: registros.filter(r => r.fechaProgramada && new Date(r.fechaProgramada) < hoy && r.estado !== "completado").length,
      correctivos: registros.filter(r => r.tipo === "correctivo").length,
      preventivos: registros.filter(r => r.tipo === "preventivo").length,
      mesMes: registros.filter(r => {
        try { const f = new Date(r.fecha); return f.getMonth() === mesActual && f.getFullYear() === anioActual; } catch { return false; }
      }).length,
      mttr: calcularMTTR(registros),
      mtbf: calcularMTBF(registros),
    };
  }, [registros]);

  const oeeCalc = calcularOEE(oeeForm.disponibilidad, oeeForm.rendimiento, oeeForm.calidad);

  const validar = () => {
    const e = {};
    if (!form.maquina?.trim()) e.maquina = "Requerido";
    if (!form.descripcion?.trim()) e.descripcion = "Requerido";
    if (!form.responsable?.trim()) e.responsable = "Requerido";
    if (!form.fecha) e.fecha = "Requerido";
    setErrores(e);
    return Object.keys(e).length === 0;
  };
  if (cargandoTPM) return (
  <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:bg }}>
    <p style={{ color: oscuro?"white":"#09184D", fontSize:"16px" }}>Cargando TPM...</p>
  </div>
);

  const guardar = () => {
    if (!validar()) return;
    try {
      const actuales = storage.get(STORAGE_TPM);
      const nuevo = {
        ...form,
        id: Date.now(),
        idTPM: generarIdTPM(actuales),
        fechaCreacion: new Date().toISOString(),
        creadoPor: usuario?.nombre || "Sistema",
        _source: "localStorage",
        _schema: "tpm_v1",
      };
      const nuevos = [...actuales, nuevo];
      storage.set(STORAGE_TPM, nuevos);
      setRegistros(nuevos);
      subirDatos(STORAGE_TPM);
      logAction("TPM", "crear", { entidad: "OrdenTPM", idEntidad: nuevo.idTPM, despues: nuevo }, usuario);

      // Si es correctivo con NC vinculada → generar NC automática
      if (form.tipo === "correctivo" && form.generarNC) {
        const ncs = storage.get(STORAGE_NC);
        const anio = new Date().getFullYear();
        const nci = `NCI-${anio}-${String(ncs.length + 1).padStart(3, "0")}`;
        const ncTPM = {
          nci, id: Date.now() + 1,
          origen: "TPM — Falla de equipo",
          tipoNC: "Proceso fuera de especificación",
          maquinaId: form.maquinaId || null,
          maquinaNombre: form.maquinaId ? form.maquina : null,
          proceso: `Mantenimiento — ${form.maquina}`,
          descripcion: `Falla correctiva en ${form.maquina}: ${form.descripcion}`,
          severidad: form.severidadFalla || "mayor",
          escalamiento: "medio",
          estado: "abierta",
          idTPM: nuevo.idTPM,
          fechaHora: new Date().toISOString().slice(0, 16),
          creadoPor: usuario?.nombre || "Sistema",
          rolCreador: usuario?.rol || "",
          generadaAutomaticamente: true,
          _generadaDesde: "tpm",
          _source: "localStorage",
          _schema: "nc_v1",
        };
        storage.set(STORAGE_NC, [...ncs, ncTPM]);
        subirDatos(STORAGE_NC);
        alert(`NC generada automáticamente: ${nci}`);
      }

      setForm({ tipo: "preventivo", pilar: "planificado", estado: "pendiente", fecha: fechaHoy() });
      setErrores({});
      setVista("listado");
    } catch (err) { LOG.error("Error guardar TPM", err); alert("Error inesperado."); }
  };

  const cambiarEstado = (id, nuevoEstado) => {
    const actualizados = registros.map(r => r.id === id ? {
      ...r, estado: nuevoEstado,
      ...(nuevoEstado === "completado" ? { fechaCompletado: new Date().toISOString() } : {}),
    } : r);
    setRegistros(actualizados);
    storage.set(STORAGE_TPM, actualizados);
    subirDatos(STORAGE_TPM);
    if (detalle?.id === id) setDetalle(prev => ({ ...prev, estado: nuevoEstado }));
    logAction("TPM", "cambiar_estado", { entidad: "OrdenTPM", idEntidad: registros.find(r => r.id === id)?.idTPM, despues: { estado: nuevoEstado } }, usuario);
  };

  const eliminar = async (id) => {
    const nuevos = registros.filter(r => r.id !== id);
    setRegistros(nuevos);
    storage.set(STORAGE_TPM, nuevos);
    await eliminarDato(STORAGE_TPM, id);
    setConfirmEliminar(null); setDetalle(null); setVista("listado");
  };

  const registrosFiltrados = filtroTipo === "todos" ? registros : registros.filter(r => r.tipo === filtroTipo);

  const Header = ({ titulo, onBack, extra }) => (
    <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Volver</button>
        <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>{titulo}</span>
      </div>
      {extra}
    </div>
  );

  if (confirmEliminar) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: cardBg, borderRadius: "16px", padding: "2rem", maxWidth: "360px", width: "90%", border: `1px solid ${borde}`, textAlign: "center" }}>
        <p style={{ fontSize: "32px", marginBottom: "1rem" }}>🔧</p>
        <p style={{ fontSize: "15px", fontWeight: "600", color: texto, marginBottom: ".5rem" }}>¿Eliminar este registro TPM?</p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "1.5rem" }}>
          <button onClick={() => setConfirmEliminar(null)} style={{ padding: "10px 20px", background: "none", border: `1px solid ${borde}`, borderRadius: "10px", cursor: "pointer", color: texto, fontSize: "13px" }}>Cancelar</button>
          <button onClick={() => eliminar(confirmEliminar)} style={{ padding: "10px 20px", background: "#e63946", border: "none", borderRadius: "10px", cursor: "pointer", color: "white", fontSize: "13px", fontWeight: "600" }}>Eliminar</button>
        </div>
      </div>
    </div>
  );

  if (vista === "detalle" && detalle) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo={detalle.idTPM}
        onBack={() => { setDetalle(null); setVista("listado"); }}
        extra={esAdmin && <button onClick={() => setConfirmEliminar(detalle.id)} style={{ background: "rgba(230,57,70,.2)", border: "1px solid rgba(230,57,70,.4)", color: "#e63946", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "12px" }}>🗑 Eliminar</button>}
      />
      <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
        <div style={{ ...card, border: `1px solid ${TIPOS_MANT[detalle.tipo]?.color || "#7B5BF2"}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <h3 style={{ fontSize: "14px", fontWeight: "700", color: texto, margin: 0 }}>{detalle.maquina}</h3>
            <span style={{ fontSize: "12px", fontWeight: "700", padding: "3px 10px", borderRadius: "10px", background: `${TIPOS_MANT[detalle.tipo]?.color}22`, color: TIPOS_MANT[detalle.tipo]?.color, border: `1px solid ${TIPOS_MANT[detalle.tipo]?.color}` }}>
              {TIPOS_MANT[detalle.tipo]?.label}
            </span>
          </div>
          {[["ID TPM", detalle.idTPM], ["Pilar TPM", PILARES_TPM[detalle.pilar]?.label], ["Máquina / Equipo", detalle.maquina], ["Sector", detalle.sector], ["Responsable", detalle.responsable], ["Fecha", detalle.fecha], ["Fecha programada", detalle.fechaProgramada], ["Tiempo estimado (hs)", detalle.tiempoEstimado], ["Tiempo reparación (hs)", detalle.tiempoReparacion], ["Tiempo entre fallas (hs)", detalle.tiempoEntreF], ["Creado por", detalle.creadoPor]].map(([k, v]) => v && (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "4px 0", borderBottom: `0.5px solid ${borde}` }}>
              <span style={{ color: subtexto }}>{k}</span>
              <span style={{ color: texto, fontWeight: "500" }}>{v}</span>
            </div>
          ))}
        </div>
        <div style={card}>
          <p style={{ fontSize: "11px", color: subtexto, fontWeight: "600", marginBottom: "6px" }}>DESCRIPCIÓN</p>
          <p style={{ fontSize: "13px", color: texto, margin: 0 }}>{detalle.descripcion}</p>
          {adjuntosDe(detalle).length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "8px" }}>
              {adjuntosDe(detalle).map((a, i) => (
                <button key={i} onClick={() => verFotoTPM(a)} style={{ padding: 0, background: "none", border: "none", color: "#3b82f6", cursor: "pointer", fontSize: "12px", textAlign: "left" }}>
                  {a.tipo === "application/pdf" ? "📄" : "📷"} Ver {a.nombre || "adjunto del reporte"}
                </button>
              ))}
            </div>
          )}
          {detalle.observaciones && <>
            <p style={{ fontSize: "11px", color: subtexto, fontWeight: "600", marginTop: "12px", marginBottom: "6px" }}>OBSERVACIONES</p>
            <p style={{ fontSize: "13px", color: texto, margin: 0 }}>{detalle.observaciones}</p>
          </>}
        </div>
        {/* GENERAR OC TPM */}
        {esAdmin && (
          <div style={{ ...card, border:"1px solid #22c55e40", background: oscuro?"#0a1f0a":"#f0fff4" }}>
            <h3 style={{ fontSize:"13px", fontWeight:"700", color:"#22c55e", marginBottom:"8px" }}>🛒 Generar OC de repuestos/servicio</h3>
            <button onClick={()=>setModalOCTPM(detalle)}
              style={{ padding:"8px 16px", background:"#22c55e", color:"white", border:"none", borderRadius:"8px", cursor:"pointer", fontSize:"12px", fontWeight:"700" }}>
              + Generar OC para este mantenimiento
            </button>
            {modalOCTPM && (
              <ModalGenerarOC oscuro={oscuro} usuario={usuario} origen="TPM"
                referencia={modalOCTPM.idTPM} categoriaDefault="Mantenimiento"
                onClose={()=>setModalOCTPM(null)}
                onCreada={(cod)=>{ alert(`✅ OC generada: ${cod} — revisá Compras`); setModalOCTPM(null); }}
              />
            )}
          </div>
        )}
        {esAdmin && (
          <div style={card}>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#7B5BF2", marginBottom: "10px" }}>Estado del trabajo</h3>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {Object.entries(ESTADOS_OT).map(([val, { label, color }]) => (
                <button key={val} onClick={() => cambiarEstado(detalle.id, val)}
                  style={{ padding: "7px 12px", borderRadius: "10px", fontSize: "12px", cursor: "pointer", fontWeight: "600", border: detalle.estado === val ? `2px solid ${color}` : `1px solid ${borde}`, background: detalle.estado === val ? `${color}22` : cardBg, color: detalle.estado === val ? color : subtexto }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (vista === "nuevo") return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo="Nueva Orden TPM" onBack={() => { setVista("listado"); setErrores({}); }} />
      <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#F0EEFF", border: `1px solid ${oscuro ? "#7B5BF2" : "#d4c8ff"}` }}>
          <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#7B5BF2", marginBottom: "4px" }}>TPM — Mantenimiento Productivo Total</h3>
          <p style={{ fontSize: "12px", color: subtexto, margin: 0 }}>Creado por: <strong style={{ color: texto }}>{usuario?.nombre}</strong> — {usuario?.rol}</p>
        </div>

        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "10px", color: "#09184D" }}>Tipo de mantenimiento</h3>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {Object.entries(TIPOS_MANT).map(([val, { label, color }]) => (
              <button key={val} onClick={() => setForm(f => ({ ...f, tipo: val }))}
                style={{ flex: 1, minWidth: "100px", padding: "8px 10px", borderRadius: "10px", fontSize: "12px", cursor: "pointer", fontWeight: "700", border: form.tipo === val ? `2px solid ${color}` : `1px solid ${borde}`, background: form.tipo === val ? `${color}22` : cardBg, color: form.tipo === val ? color : texto }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "10px", color: "#7B5BF2" }}>Pilar TPM</h3>
          {Object.entries(PILARES_TPM).map(([val, { label, color, desc }]) => (
            <button key={val} onClick={() => setForm(f => ({ ...f, pilar: val }))}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", marginBottom: "6px", fontSize: "12px", cursor: "pointer", textAlign: "left", border: form.pilar === val ? `2px solid ${color}` : `1px solid ${borde}`, background: form.pilar === val ? `${color}18` : cardBg, color: texto }}>
              <span style={{ fontWeight: "700", color }}>{label}</span>
              <span style={{ display: "block", fontSize: "10px", color: subtexto, marginTop: "2px" }}>{desc}</span>
            </button>
          ))}
        </div>

        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#22c55e" }}>Datos del trabajo</h3>
          <span style={lbl}>Máquina / Equipo * {errores.maquina && <span style={{ color: "#e63946" }}>— {errores.maquina}</span>}</span>
          <input value={form.maquina || ""} onChange={e => setForm(f => ({ ...f, maquina: e.target.value }))} style={{ ...inp, borderColor: errores.maquina ? "#e63946" : borde }} placeholder="Ej: Offset Heidelberg CD102" />
          {maquinas.length > 0 && (
            <>
              <span style={lbl}>Vincular a máquina cargada (opcional — habilita el indicador de confiabilidad en Máquinas)</span>
              <select value={form.maquinaId || ""} onChange={e => {
                const id = e.target.value ? Number(e.target.value) : null;
                const m = maquinas.find(x => x.id === id);
                setForm(f => ({ ...f, maquinaId: id, maquina: m ? (m.nombre + (m.modelo ? ` — ${m.modelo}` : "")) : f.maquina }));
              }} style={inp}>
                <option value="">— Sin vincular —</option>
                {maquinas.map(m => <option key={m.id} value={m.id}>{m.nombre}{m.modelo ? ` — ${m.modelo}` : ""}</option>)}
              </select>
            </>
          )}
          <span style={lbl}>Sector</span>
          <input onChange={e => setForm(f => ({ ...f, sector: e.target.value }))} style={inp} placeholder="Ej: Impresión" />
          <span style={lbl}>Descripción del trabajo * {errores.descripcion && <span style={{ color: "#e63946" }}>— {errores.descripcion}</span>}</span>
          <textarea rows={3} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} style={{ ...inp, resize: "none", borderColor: errores.descripcion ? "#e63946" : borde }} placeholder="Describí el trabajo de mantenimiento a realizar o realizado..." />
          <span style={lbl}>Responsable * {errores.responsable && <span style={{ color: "#e63946" }}>— {errores.responsable}</span>}</span>
          <input onChange={e => setForm(f => ({ ...f, responsable: e.target.value }))} style={{ ...inp, borderColor: errores.responsable ? "#e63946" : borde }} />
          <span style={lbl}>Fecha *</span>
          <input type="date" defaultValue={fechaHoy()} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} style={inp} />
          <span style={lbl}>Fecha programada (si es preventivo)</span>
          <input type="date" onChange={e => setForm(f => ({ ...f, fechaProgramada: e.target.value }))} style={inp} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <div>
              <span style={lbl}>Tiempo estimado (hs)</span>
              <input type="number" min="0" step="0.5" onChange={e => setForm(f => ({ ...f, tiempoEstimado: e.target.value }))} style={inp} placeholder="0.0" />
            </div>
            <div>
              <span style={lbl}>Tiempo real de reparación (hs)</span>
              <input type="number" min="0" step="0.5" onChange={e => setForm(f => ({ ...f, tiempoReparacion: e.target.value }))} style={inp} placeholder="0.0" />
            </div>
          </div>
          <span style={lbl}>Tiempo entre fallas (hs) — para MTBF</span>
          <input type="number" min="0" onChange={e => setForm(f => ({ ...f, tiempoEntreF: e.target.value }))} style={inp} placeholder="Horas desde la última falla del mismo equipo" />
          <span style={lbl}>Observaciones</span>
          <textarea rows={2} onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))} style={{ ...inp, resize: "none" }} />
        </div>

        {form.tipo === "correctivo" && (
          <div style={{ ...card, background: oscuro ? "#1a0505" : "#fff5f5", border: "1px solid #e63946" }}>
            <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#e63946", marginBottom: "8px" }}>⚠️ Falla correctiva — ¿Generar NC automática?</h3>
            <p style={{ fontSize: "12px", color: subtexto, marginBottom: "10px" }}>Las fallas correctivas pueden vincularse al módulo de No Conformidades para trazabilidad completa.</p>
            <div style={{ display: "flex", gap: "8px" }}>
              {[["si", "✅ Sí, generar NC"], ["no", "❌ No"]].map(([val, lab]) => (
                <button key={val} onClick={() => setForm(f => ({ ...f, generarNC: val === "si" }))}
                  style={{ flex: 1, padding: "8px", borderRadius: "10px", fontSize: "12px", cursor: "pointer", fontWeight: "600", border: (form.generarNC === (val === "si")) ? `2px solid ${val === "si" ? "#e63946" : "#808085"}` : `1px solid ${borde}`, background: cardBg, color: texto }}>
                  {lab}
                </button>
              ))}
            </div>
            {form.generarNC && (
              <div style={{ marginTop: "10px" }}>
                <span style={lbl}>Severidad de la falla</span>
                <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                  {[["critica", "🔴 Crítica"], ["mayor", "🟡 Mayor"], ["menor", "🟢 Menor"]].map(([val, lab]) => (
                    <button key={val} onClick={() => setForm(f => ({ ...f, severidadFalla: val }))}
                      style={{ flex: 1, padding: "6px", borderRadius: "8px", fontSize: "11px", cursor: "pointer", fontWeight: "600", border: form.severidadFalla === val ? "2px solid #e63946" : `1px solid ${borde}`, background: cardBg, color: texto }}>
                      {lab}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <button onClick={guardar} style={{ width: "100%", padding: "14px", background: "#22c55e", color: "white", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: "600", cursor: "pointer", marginTop: "1rem" }}>
          Guardar registro TPM
        </button>
      </div>
    </div>
  );

  // ─── DASHBOARD TPM ────────────────────────────────────────
  if (vista === "dashboard") return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={onVolver} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Menú</button>
          <BotonInstructivo moduloKey="tpm" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="tpm" oscuro={oscuro} />}
          <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>🔧 TPM — Mantenimiento Productivo Total</span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={() => setVista("listado")} style={{ padding: "6px 12px", background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: "white" }}>📋 Órdenes</button>
          {esAdmin && <button onClick={() => setVista("nuevo")} style={{ padding: "6px 14px", background: "#22c55e", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>+ Nueva orden</button>}
        </div>
      </div>

      <div style={{ padding: "1rem 1.5rem", maxWidth: "900px", margin: "0 auto" }}>

        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: "10px", marginBottom: "1.5rem" }}>
          {[["Total órdenes", kpis.total, "#7B5BF2"], ["Pendientes", kpis.pendientes, "#f59e0b"], ["Completados", kpis.completados, "#22c55e"], ["Vencidos", kpis.vencidos, "#e63946"], ["Correctivos", kpis.correctivos, "#e63946"], ["Preventivos", kpis.preventivos, "#3b82f6"], [`MTTR (hs)`, kpis.mttr || "—", "#f97316"], [`MTBF (hs)`, kpis.mtbf || "—", "#22c55e"]].map(([lab, val, col]) => (
            <div key={lab} style={{ background: cardBg, borderRadius: "12px", padding: "1rem", border: `1px solid ${borde}`, textAlign: "center" }}>
              <p style={{ fontSize: "20px", fontWeight: "700", color: col, margin: 0 }}>{val}</p>
              <p style={{ fontSize: "10px", color: subtexto, margin: "4px 0 0" }}>{lab}</p>
            </div>
          ))}
        </div>

        {/* CALCULADORA OEE */}
        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#f0f8ff", border: `1px solid ${oscuro ? "#1e3a8a" : "#bde0fe"}` }}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#3b82f6", marginBottom: "12px" }}>
            📊 Calculadora OEE — Efectividad Global del Equipo
          </h3>
          <p style={{ fontSize: "12px", color: subtexto, marginBottom: "12px" }}>OEE = Disponibilidad × Rendimiento × Calidad · Clase mundial ≥ 85%</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginBottom: "12px" }}>
            {[{ key: "disponibilidad", label: "Disponibilidad (%)", color: "#22c55e" }, { key: "rendimiento", label: "Rendimiento (%)", color: "#3b82f6" }, { key: "calidad", label: "Calidad (%)", color: "#7B5BF2" }].map(({ key, label, color }) => (
              <div key={key}>
                <span style={{ fontSize: "11px", color, fontWeight: "600", display: "block", marginBottom: "4px" }}>{label}</span>
                <input type="number" min="0" max="100" value={oeeForm[key]} onChange={e => setOeeForm(f => ({ ...f, [key]: e.target.value }))} style={{ ...inp, marginTop: 0, textAlign: "center", fontWeight: "700", fontSize: "16px" }} placeholder="0-100" />
              </div>
            ))}
          </div>
          {(oeeForm.disponibilidad && oeeForm.rendimiento && oeeForm.calidad) && (
            <div style={{ padding: "16px", borderRadius: "12px", background: `${nivelOEE(oeeCalc).color}18`, border: `2px solid ${nivelOEE(oeeCalc).color}`, textAlign: "center" }}>
              <p style={{ fontSize: "12px", color: subtexto, margin: "0 0 4px" }}>OEE calculado</p>
              <p style={{ fontSize: "36px", fontWeight: "700", color: nivelOEE(oeeCalc).color, margin: "0 0 4px" }}>{oeeCalc}%</p>
              <p style={{ fontSize: "13px", fontWeight: "700", color: nivelOEE(oeeCalc).color, margin: 0 }}>{nivelOEE(oeeCalc).label}</p>
            </div>
          )}
        </div>

        {/* PILARES TPM */}
        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", color: texto, marginBottom: "14px" }}>🏛️ Pilares TPM — Estado actual</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "10px" }}>
            {Object.entries(PILARES_TPM).map(([key, { label, color, desc }]) => {
              const cant = registros.filter(r => r.pilar === key).length;
              return (
                <div key={key} style={{ background: oscuro ? "#091840" : "#f8f9fa", borderRadius: "10px", padding: "12px", border: `1px solid ${cant > 0 ? color : borde}` }}>
                  <p style={{ fontSize: "12px", fontWeight: "700", color: cant > 0 ? color : subtexto, margin: "0 0 4px" }}>{label}</p>
                  <p style={{ fontSize: "10px", color: subtexto, margin: "0 0 6px" }}>{desc}</p>
                  <p style={{ fontSize: "18px", fontWeight: "700", color: cant > 0 ? color : subtexto, margin: 0 }}>{cant} <span style={{ fontSize: "10px", fontWeight: "400" }}>registros</span></p>
                </div>
              );
            })}
          </div>
        </div>

        {/* CONECTOR SISPRO */}
        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#faf5ff", border: `1px solid ${oscuro ? "#7B5BF2" : "#e9d5ff"}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#7B5BF2", marginBottom: "6px" }}>
                🔌 Conector SISPRO — Integración futura preparada
              </h3>
              <p style={{ fontSize: "12px", color: subtexto, margin: "0 0 10px" }}>
                El sistema está preparado para recibir datos de producción, eficiencia y mantenimiento desde SISPRO u otros ERP gráficos.
              </p>
            </div>
            <span style={{ fontSize: "10px", fontWeight: "700", background: "#fef3c7", color: "#92400e", padding: "3px 8px", borderRadius: "8px", whiteSpace: "nowrap", marginLeft: "12px" }}>Planificado v2.0</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "8px" }}>
            {[
              { icono: "📥", titulo: "Importación CSV", desc: "Ya funcional — subís datos desde Excel" },
              { icono: "🔗", titulo: "API REST", desc: "Endpoint listo para conectar con SISPRO" },
              { icono: "⚡", titulo: "Datos en tiempo real", desc: "OEE, eficiencia y producción automáticos" },
              { icono: "📊", titulo: "Dashboard unificado", desc: "KPIs de SISPRO + ALQOS™ en un panel" },
            ].map(({ icono, titulo, desc }) => (
              <div key={titulo} style={{ background: cardBg, borderRadius: "10px", padding: "10px", border: `1px solid ${borde}` }}>
                <p style={{ fontSize: "18px", margin: "0 0 4px" }}>{icono}</p>
                <p style={{ fontSize: "11px", fontWeight: "700", color: "#7B5BF2", margin: "0 0 3px" }}>{titulo}</p>
                <p style={{ fontSize: "10px", color: subtexto, margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // ─── LISTADO ──────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={() => setVista("dashboard")} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Dashboard</button>
          <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>Órdenes TPM</span>
        </div>
        <CampanaNotificaciones oscuro={oscuro} modulo="TPM" />
          {esAdmin && <button onClick={() => setVista("nuevo")} style={{ padding: "6px 14px", background: "#22c55e", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>+ Nueva</button>}
      </div>
      <div style={{ padding: "1rem 1.5rem" }}>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "1rem" }}>
          <button onClick={() => setFiltroTipo("todos")} style={{ padding: "4px 10px", borderRadius: "16px", fontSize: "11px", cursor: "pointer", fontWeight: "600", border: filtroTipo === "todos" ? "2px solid #7B5BF2" : `1px solid ${borde}`, background: filtroTipo === "todos" ? "rgba(123,91,242,.1)" : cardBg, color: filtroTipo === "todos" ? "#7B5BF2" : subtexto }}>Todos</button>
          {Object.entries(TIPOS_MANT).map(([val, { label, color }]) => (
            <button key={val} onClick={() => setFiltroTipo(val)} style={{ padding: "4px 10px", borderRadius: "16px", fontSize: "11px", cursor: "pointer", fontWeight: "600", border: filtroTipo === val ? `2px solid ${color}` : `1px solid ${borde}`, background: filtroTipo === val ? `${color}22` : cardBg, color: filtroTipo === val ? color : subtexto }}>{label}</button>
          ))}
        </div>
        {registrosFiltrados.length === 0
          ? <div style={{ textAlign: "center", padding: "4rem" }}>
              <p style={{ fontSize: "40px" }}>🔧</p>
              <p style={{ fontSize: "15px", color: texto }}>No hay órdenes TPM registradas</p>
              <p style={{ fontSize: "13px", color: subtexto }}>Creá la primera orden de mantenimiento</p>
            </div>
          : registrosFiltrados.map(r => {
              const vencida = r.fechaProgramada && new Date(r.fechaProgramada) < new Date() && r.estado !== "completado";
              return (
                <div key={r.id} onClick={() => { setDetalle(r); setVista("detalle"); }}
                  style={{ background: vencida ? (oscuro ? "#1a0505" : "#fff5f5") : cardBg, borderRadius: "12px", padding: "1.2rem 1.5rem", border: `1px solid ${vencida ? "#e63946" : borde}`, marginBottom: "10px", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(34,197,94,0.15)"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <span style={{ fontWeight: "700", color: "#22c55e", fontSize: "13px" }}>{r.idTPM}</span>
                      <span style={{ marginLeft: "8px", fontSize: "11px", fontWeight: "700", color: TIPOS_MANT[r.tipo]?.color, background: `${TIPOS_MANT[r.tipo]?.color}22`, padding: "2px 6px", borderRadius: "6px" }}>{TIPOS_MANT[r.tipo]?.label}</span>
                      <p style={{ fontSize: "13px", color: texto, fontWeight: "600", marginTop: "4px" }}>{r.maquina}</p>
                      <p style={{ fontSize: "12px", color: subtexto, marginTop: "2px" }}>{r.descripcion?.slice(0, 60)}{r.descripcion?.length > 60 ? "..." : ""}</p>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
                      <span style={{ fontSize: "11px", fontWeight: "700", color: ESTADOS_OT[r.estado]?.color }}>{ESTADOS_OT[r.estado]?.label}</span>
                      <span style={{ fontSize: "10px", color: subtexto }}>{PILARES_TPM[r.pilar]?.label?.split(" ").slice(1).join(" ")}</span>
                      {vencida && <span style={{ fontSize: "10px", color: "#e63946", fontWeight: "700" }}>⏰ VENCIDA</span>}
                    </div>
                  </div>
                  <div style={{ marginTop: "6px", fontSize: "11px", color: oscuro ? "#22c55e" : "#4ade80" }}>
                    {r.responsable} · {r.fecha} {r.sector ? `· ${r.sector}` : ""}
                  </div>
                </div>
              );
            })
        }
      </div>
    </div>
  );
}